import styles from './styles.module.css';
import { motion, AnimatePresence } from 'motion/react';
import useVehicleCompare from '../../utils/VehicleCompareProvider';
import { useEffect, useState } from 'react';
import client from '../../utils/client';
import { useQuery } from '@tanstack/react-query';
import { Vehicle } from '../../types';
import { XCircleIcon, XIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { reviewNotes } from '../../utils/review';
import Note from '../Note';
import { router } from '@inertiajs/react';
import carIcon from '../../assets/images/car_icon.svg';
import ImageFallback from '../ImageFallback/ImageFallback';
import { compareMeta } from './utils';
import cn from 'classnames/bind';
const cx = cn.bind(styles);

const VehicleThumbnail: React.FC<{ vehicle: Vehicle; className?: string }> = ({ vehicle, className }) => {
  const [imageError, setImageError] = useState(false);

  if (imageError || !vehicle.photo_url) {
    return <ImageFallback className={className} />;
  }

  return (
    <img
      className={className}
      src={vehicle.photo_url}
      alt={vehicle?.brand?.title}
      onError={() => setImageError(true)}
    />
  );
};

const motionProps = {
  initial: {
    y: '100%',
    opacity: 0
  },
  animate: {
    y: 0,
    opacity: 1
  },
  exit: {
    y: '100%',
    opacity: 0
  },
  transition: { type: 'spring' as const, stiffness: 300, damping: 30 }
}

const getVehicles = async ({ queryKey }: { queryKey: [string, string[]] }) => {
  const [, ids] = queryKey;
  const { data } = await client.get('/vehicles/comparator', { params: { ids } });
  const vehicles = (data?.vehicles || []) as Vehicle[];
  return vehicles.sort((a, b) => ids.indexOf(compareMeta(a).key) - ids.indexOf(compareMeta(b).key));
};

const clearVehicles = async () => {
  const response = await client.delete('/vehicles/comparator');
  return response.data;
}

type VehicleItemProps = {
  vehicle: Vehicle;
  onRemove: (id: string) => void;
}

const VehicleItem: React.FC<VehicleItemProps> = ({ vehicle, onRemove }) => {
  const { label } = compareMeta(vehicle);
  const handleRemove = () => onRemove(vehicle.id);

  return (
    <div className={styles.vehicleItem}>
      <button className={styles.removeButton} onClick={handleRemove} type="button">
        <XIcon className="size-4" />
      </button>
      <VehicleThumbnail vehicle={vehicle} />
      <h3>
        {vehicle?.brand?.title} - {vehicle.name}
        {label && <span className="text-sm text-gray-500 block mt-1">{label}</span>}
      </h3>
    </div>
  );
};

const VehicleCompareTable: React.FC<{ vehicles: Vehicle[], onRemove: (id: string) => void, closeComparison: () => void }> = ({ vehicles, onRemove, closeComparison }) => {
  const handleVisitDetail = (vehicule: Vehicle) => {
    router.visit(compareMeta(vehicule).detailUrl)
    closeComparison();
  }

  return (
    <table className={styles.table}>
      <tbody>
        <tr>
          <td></td>
          {vehicles.map((vehicle: Vehicle) => (
            <td className="text-center" key={compareMeta(vehicle).key}>
              <div className={styles.vehicleItem}>
                <button className={styles.removeButton} onClick={() => onRemove(vehicle.id)} type="button">
                  <XIcon className="size-4" />
                </button>
                <VehicleThumbnail vehicle={vehicle} className={styles.vehicleImage} />
              </div>
            </td>
          ))}
        </tr>
        <tr>
          <td className=""></td>
          {vehicles.map((vehicle: Vehicle) => {
            const label = compareMeta(vehicle).label;
            return (
              <td className="text-center font-semibold" key={compareMeta(vehicle).key}>
                <div>{vehicle?.brand?.title} - {vehicle.name}</div>
                {label && <div className="text-gray-500 block mt-1 text-sm">{label}</div>}
              </td>
            );
          })}
        </tr>
        <tr>
          <td className="text-left font-semibold">
            Note moyenne
          </td>
          {vehicles.map((vehicle: Vehicle) => (
            <td className="text-center" key={`${compareMeta(vehicle).key}-note`}>
              <Note size="small" note={vehicle.note} />
            </td>
          ))}
        </tr>
        {
          reviewNotes.map((note, index) => (
            <tr key={note.name} className={cx({
              odd: index % 2 === 0,
              even: index % 2 !== 0,
            })}>
              <td className="text-left font-semibold">
                {note.label}
              </td>
              {vehicles.map((vehicle: Vehicle) => (
                <td className="text-center" key={`${compareMeta(vehicle).key}-${note.name}`}>
                  <Note
                    size="small"
                    note={vehicle.notes?.[note.name as keyof typeof vehicle.notes] || 0}
                  />
                </td>
              ))}
            </tr>
          ))
        }
        <tr>
          <td className="text-left font-semibold"></td>
          {vehicles.map((vehicle: Vehicle) => (
            <td className="text-center font-semibold" key={compareMeta(vehicle).key}>
              <Button onClick={() => handleVisitDetail(vehicle)}>
                <img src={carIcon} alt="logo" className="w-5 block lg:hidden xl:block" />
                Voir la fiche complète
              </Button>
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  )
}

const VehicleComparator = () => {
  const { ids } = useVehicleCompare();
  const [comparisonVisible, setComparisonVisible] = useState(false);

  const { removeId, clearIds } = useVehicleCompare();

  const hasVehicles = ids.length > 0;

  const {
    data: vehicles,
  } = useQuery({
    queryKey: ['vehicles', ids],
    queryFn: getVehicles,
    placeholderData: (previousData) => previousData,
    enabled: !!hasVehicles,
  })

  useEffect(() => {
    if (vehicles && vehicles.length < 2) setComparisonVisible(false);
  }, [vehicles]);

  const toggleComparisonVisible = () => {
    setComparisonVisible((prev) => !prev);
  }

  const onClear = () => {
    clearIds();
    setComparisonVisible(false);
    clearVehicles();
  }

  if (!vehicles || vehicles.length === 0) return null;

  const Header = (
    <div className={styles.header}>
      <h2>Comparer des véhicules</h2>
      <div className="flex flex-row gap-2">
        <Button onClick={toggleComparisonVisible}>
          {comparisonVisible ? 'Masquer' : 'Comparer'}
        </Button>
        <Button
          variant="outline"
          onClick={onClear}
        >
          <XCircleIcon className="size-4" />
          Tout effacer
        </Button>
      </div>
    </div>
  )

  return (
    <AnimatePresence>
      {hasVehicles && (
        <motion.div
          className={cx(styles.vehicleComparator, {
            compareDetails: comparisonVisible,
          })}
          {...motionProps}
        >
          <div className={styles.vehicleComparatorContent}>
            {Header}
            {comparisonVisible ? (
              <div className={styles.content}>
                <VehicleCompareTable vehicles={vehicles} onRemove={removeId} closeComparison={toggleComparisonVisible} />
              </div>
            ) : (
              <div className={styles.content}>
                {vehicles.map((vehicle: Vehicle) => (
                  <VehicleItem key={compareMeta(vehicle).key} vehicle={vehicle} onRemove={removeId} />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default VehicleComparator;